import {
  Box,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Text as ChakraText,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { BsThreeDotsVertical } from 'react-icons/bs'
import { FiEdit } from 'react-icons/fi'
import { RiDeleteBinFill } from 'react-icons/ri'
import type { Column } from 'react-table'
import { useTable } from 'react-table'
import useFetch, { CachePolicies } from 'use-http'
import { colors } from '../../../../ui-kit'
import { checkPermission, PermissionsEnum } from '../../../../utils'
import useToast from '../../../hooks/useToast'
import type { CmFieldResponseObject } from '../../../interfaces'
import { UserContext } from '../../../providers'
import { CustomMetadataContext } from '../../../providers/CustomMetadataProvider'
import PermissionGuard from '../../PermissionGuard'
import DeleteDialog from './DeleteDialog'
import FormModal from './FormModal'

const CustomMetadataSettings = () => {
  /*
    State variables
  */
  const {
    isOpen: formModalOpen,
    onOpen: openFormModal,
    onClose: closeFormModal,
  } = useDisclosure()
  const {
    isOpen: deleteDialogOpen,
    onOpen: openDeleteDialog,
    onClose: closeDeleteDialog,
  } = useDisclosure()
  const [currentlyEditingItem, setCurrentlyEditingItem] = useState<string>('')
  const [currentlyDeletingItem, setCurrentlyDeletingItem] = useState<string>('')
  const { refetch } = useContext(CustomMetadataContext)
  /*
    Utilities
  */
  const toast = useToast()
  const { t } = useTranslation()
  const { user, refetch: userRefetch } = useContext(UserContext)

  /*
    API Handling
  */
  const {
    data: cmGetRequestApiResponse,
    request: cmGetRequest,
    loading: cmGetRequestLoading,
  } = useFetch<CmFieldResponseObject[]>('/api/v1/customMetadataFields', {
    method: 'get',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const {
    data: cmPostRequestApiResponse,
    request: cmPostRequest,
    loading: cmPostRequestLoading,
  } = useFetch<CmFieldResponseObject[]>('/api/v1/customMetadataFields', {
    method: 'post',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const {
    data: cmPatchRequestApiResponse,
    request: cmPatchRequest,
    loading: cmPatchRequestLoading,
  } = useFetch<CmFieldResponseObject[]>('/api/v1/customMetadataFields', {
    method: 'patch',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const {
    data: cmDeleteRequestApiResponse,
    request: cmDeleteRequest,
    loading: cmDeleteRequestLoading,
  } = useFetch<CmFieldResponseObject[]>('/api/v1/customMetadataFields', {
    method: 'delete',
    cachePolicy: CachePolicies.NO_CACHE,
    data: [],
  })

  const getCustomMetadata = async () => {
    await cmGetRequest.get()

    if (cmGetRequest.error) {
      toast({
        description: t(
          'error_in_fetching_custom_metadata',
          'Error in fetching custom metadata',
        ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      throw new Error()
    }

    return
  }

  const createCustomMetadataField = async (
    formValues: CmFieldResponseObject,
  ) => {
    await cmPostRequest.post('/', formValues)

    if (cmPostRequest.error) {
      toast({
        description: (cmPostRequest as any)?.data?.message
          ? (cmPostRequest as any).data.message
          : t(
              'error_in_creating_custom_metadata',
              'Error in creating custom metadata.',
            ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      throw new Error()
    }

    toast({
      description: t(
        'cm_field_created_successfully',
        'Custom metadata field created successfully!',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
    void getCustomMetadata()
    refetch()
    userRefetch()
    return
  }

  const editCustomMetadataField = async (formValues: CmFieldResponseObject) => {
    await cmPatchRequest.patch(`/${formValues.id}`, {
      label: formValues.label,
      schema: formValues.schema,
    })

    if (cmPatchRequest.error) {
      toast({
        description: (cmPatchRequest as any)?.data?.message
          ? (cmPatchRequest as any).data.message
          : t(
              'error_in_updating_custom_metadata',
              'Error in updating custom metadata.',
            ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      throw new Error()
    }

    toast({
      description: t(
        'cm_field_updated_successfully',
        'Custom metadata field updated successfully!',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
    void getCustomMetadata()
    refetch()
    userRefetch()
    return
  }

  const deleteCustomMetadataField = async () => {
    if (!currentlyDeletingItem) {
      return
    }

    await cmDeleteRequest.delete(`/${currentlyDeletingItem}`)

    if (cmDeleteRequest.error) {
      toast({
        description: (cmDeleteRequest as any)?.data?.message
          ? (cmDeleteRequest as any).data.message
          : t(
              'error_in_deleting_custom_metadata',
              'Error in deleting custom metadata.',
            ),
        status: 'error',
        isClosable: true,
        duration: 5000,
        position: 'top-right',
        variant: 'outline',
      })
      throw new Error()
    }

    toast({
      description: t(
        'cm_field_deleted_successfully',
        'Custom metadata field deleted successfully!',
      ),
      status: 'success',
      isClosable: true,
      duration: 5000,
      position: 'top-right',
      variant: 'outline',
    })
    void getCustomMetadata()
    refetch()
    userRefetch()
    return
  }

  /*
    Effects
  */
  useEffect(() => {
    void getCustomMetadata()
  }, [])

  /*
    Memoizations
  */
  const TableHeader = useMemo(
    () =>
      ({
        translationKey,
        mainText,
      }: {
        translationKey: string
        mainText: string
      }): React.ReactElement =>
        (
          <ChakraText color={colors.appDarkIndigo} fontWeight="medium">
            {t(translationKey, mainText)}
          </ChakraText>
        ),
    [],
  )

  const reactTableColumns: Array<Column<CmFieldResponseObject>> =
    useMemo(() => {
      const columns: Array<Column<CmFieldResponseObject>> = [
        {
          Header: (
            <TableHeader
              translationKey="custom_metadata_field"
              mainText="Custom metadata field"
            />
          ),
          accessor: 'label',
          Cell: ({ row }) => (
            <Tooltip label={row.original.label}>
              <ChakraText isTruncated>{row.original.label}</ChakraText>
            </Tooltip>
          ),
        },
        {
          id: 'schema.type',
          Header: (
            <TableHeader translationKey="field_type" mainText="Field type" />
          ),
          accessor: row => row.schema.type,
        },
        {
          id: 'actions',
          Header: <TableHeader translationKey="actions" mainText="actions" />,
          accessor: () => '',
          Cell: ({ row }) => (
            <Menu>
              <MenuButton
                as={IconButton}
                aria-label="Options"
                icon={<BsThreeDotsVertical />}
                variant="link"
                boxShadow="none"
              />
              <MenuList>
                <MenuItem
                  icon={<FiEdit />}
                  onClick={() => {
                    setCurrentlyEditingItem(row?.original?.id ?? '')
                    openFormModal()
                  }}
                >
                  {t('edit', 'Edit')}
                </MenuItem>
                <MenuItem
                  icon={<RiDeleteBinFill />}
                  color={colors.appBrick}
                  onClick={() => {
                    setCurrentlyDeletingItem(row?.original?.id ?? '')
                    openDeleteDialog()
                  }}
                >
                  {t('delete', 'Delete')}
                </MenuItem>
              </MenuList>
            </Menu>
          ),
        },
      ]

      const hasSettingsWritePermission = checkPermission({
        user,
        permission: PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE,
      })

      if (!hasSettingsWritePermission) {
        columns.splice(2, 1)
      }

      return columns
    }, [user])

  const currentlyEditingFormValues = useMemo<
    CmFieldResponseObject | undefined
  >(() => {
    if (
      currentlyEditingItem === '' ||
      cmGetRequestLoading ||
      cmGetRequest.error
    ) {
      return undefined
    }

    return cmGetRequestApiResponse?.filter(
      cmFieldData => cmFieldData.id === currentlyEditingItem,
    )?.[0]
  }, [
    cmGetRequestApiResponse,
    currentlyEditingItem,
    cmGetRequest,
    cmGetRequestLoading,
  ])

  const currentlyDeletingFormValues = useMemo<
    CmFieldResponseObject | undefined
  >(() => {
    if (
      currentlyDeletingItem === '' ||
      cmGetRequestLoading ||
      cmGetRequest.error
    ) {
      return undefined
    }

    return cmGetRequestApiResponse?.filter(
      cmFieldData => cmFieldData.id === currentlyDeletingItem,
    )?.[0]
  }, [
    cmGetRequestApiResponse,
    currentlyDeletingItem,
    cmGetRequest,
    cmGetRequestLoading,
  ])

  /*
  Add-ons
  */
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      data: Array.isArray(cmGetRequestApiResponse)
        ? cmGetRequestApiResponse
        : [],
      columns: reactTableColumns,
    })

  return (
    <>
      <VStack alignItems="start" marginTop="1rem" spacing="0" width="full">
        <Box marginBottom="20px">
          <PermissionGuard
            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission ? (
                <Button onClick={openFormModal}>+ Add New</Button>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Box>

        <Table {...getTableProps()}>
          <Thead bgColor="appPaleGreyTwo">
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => (
                  <Th {...column.getHeaderProps()}>
                    {column.render('Header')}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <PermissionGuard
            permission={PermissionsEnum.SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) => (
              <Tbody {...getTableBodyProps()}>
                {!cmGetRequestLoading ? (
                  rows.length > 0 ? (
                    rows.map(row => {
                      prepareRow(row)
                      return row.id === 'actions' && !hasPermission ? (
                        <></>
                      ) : (
                        <Tr {...row.getRowProps()}>
                          {row.cells.map(cell => {
                            return (
                              <Td maxW="20em" {...cell.getCellProps()}>
                                {cell.render('Cell')}
                              </Td>
                            )
                          })}
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td colSpan={3} textAlign="center">
                        <ChakraText>
                          {t(
                            'no_custom_metadata_fields',
                            'No custom metadata fields added so far',
                          )}
                        </ChakraText>
                      </Td>
                    </Tr>
                  )
                ) : (
                  <Tr>
                    <Td colSpan={3} textAlign="center">
                      <Spinner
                        thickness="4px"
                        speed="0.65s"
                        emptyColor="appPaleGrey"
                        color="appBlue"
                      />
                    </Td>
                  </Tr>
                )}
              </Tbody>
            )}
          </PermissionGuard>
        </Table>
      </VStack>

      {formModalOpen ? (
        <FormModal
          isOpen={formModalOpen}
          isEditing={currentlyEditingItem !== ''}
          formValues={currentlyEditingFormValues}
          onClose={() => {
            closeFormModal()
            setCurrentlyEditingItem('')
          }}
          createCustomMetadataField={createCustomMetadataField}
          editCustomMetadataField={editCustomMetadataField}
        />
      ) : null}

      <DeleteDialog
        isOpen={deleteDialogOpen}
        isDeleting={cmDeleteRequestLoading || currentlyDeletingItem === ''}
        cmFieldName={currentlyDeletingFormValues?.name}
        closeDeleteDialog={() => {
          closeDeleteDialog()
          setCurrentlyDeletingItem('')
        }}
        deleteCustomMetadataField={deleteCustomMetadataField}
      />
    </>
  )
}

export default CustomMetadataSettings
